import { useId, useState, type ReactNode } from 'react';
import { Box, Button, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { alpha, lighten, type Theme } from '@mui/material/styles';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { fullDateTime, shortDateTime } from '../../shared/dateTime';

/**
 * Alert na górze karty zlecenia — jeden wygląd dla „Rekomendowane
 * przeplanowanie” (`ReplanAlerts`) i zmian w TMS po obsadzeniu
 * (`TmsChangeAlerts`). Przegląd karty przed prezentacją 02.10.2026: treść
 * była rozwlekła i się powtarzała, a o „Potwierdzam, widziałem” PM
 * powiedział: „umiejscowienie i wygląd jest biedny”.
 *
 * - Akcent koloru po lewej: ostrzeżenie w pomarańczu motywu, anulacja
 *   w czerwieni; obok ikona i nagłówek.
 * - Po prawej stronie nagłówka czas wykrycia (przy kilku alertach —
 *   najnowszy) i „i” z dymkiem „Skąd ten alert?”: skąd system to wie, zamiast
 *   tłumaczenia w każdym alercie.
 * - Każdy alert to jedna wyraźna linia i pod nią jedna szara linia
 *   wyjaśnienia.
 * - Stopka: jedno zdanie po lewej, przyciski po prawej, wszystkie tej samej
 *   wysokości; na wąskim ekranie przyciski zawijają się pod zdanie.
 *
 * Tu jest sam wygląd. Co pokazać, co robią przyciski i co trafia do
 * dziennika, decyduje alert, który kartę składa.
 *
 * Szary tekst idzie przez `sx`, nie przez `color="text.secondary"`: w MUI 9
 * Typography przyjmuje już tylko kolory palety i `textSecondary`, a ścieżka
 * „text.secondary” nie daje żadnego koloru — tekst wychodził czarny.
 *
 * Ważność alertów (decyzja użytkownika 12.10.2026: „wyróżnij te ważniejsze
 * alerty”): ważne idą czerwienią (`error`) i w pełni rozwinięte, zwykłe —
 * stonowane (`muted`, szare) i zwinięte w jedną linię z rozwinięciem
 * (`collapsed`). Którą ważność ma alert, mówi reguła API
 * (`apps/api/src/user-offers/alert-importance.ts`).
 */

export type OrderAlertTone = 'warning' | 'error' | 'muted';

/** Zwinięte alerty: jedna linia z liczbą, rozwijana kliknięciem. */
export interface OrderAlertCollapsed {
  /** Napis linii, np. „Inne podpowiedzi przeplanowania (12)”. */
  label: string;
  lines: readonly OrderAlertLine[];
}

/** Jeden alert: co się stało i — szarą linią pod spodem — dlaczego. */
export interface OrderAlertLine {
  /** Klucz wiersza — id wpisu, a gdy wpis rozbity na punkty, „id:numer punktu”. */
  id: number | string;
  text: string;
  /** Wyjaśnienie; `null` albo pusty napis — bez szarej linii. */
  detail: string | null;
}

/** Nazwa przycisku „i” i nagłówek jego dymka. */
export const ORIGIN_LABEL = 'Skąd ten alert?';

const TONE_ICON = {
  warning: WarningAmberRoundedIcon,
  error: ErrorOutlineRoundedIcon,
  // Podpowiedź, nie ostrzeżenie — żarówka zamiast trójkąta.
  muted: LightbulbOutlinedIcon,
} as const;

/** Kolor akcentu tonu; stonowany — szary motywu. */
const toneMain = (theme: Theme, tone: OrderAlertTone) =>
  tone === 'muted' ? theme.palette.grey[500] : theme.palette[tone].main;

/** Tło karty; stonowany — tło najechania, jak wiersz listy. */
const toneBackground = (theme: Theme, tone: OrderAlertTone) =>
  tone === 'muted' ? theme.palette.action.hover : lighten(theme.palette[tone].light, 0.9);

/**
 * Przycisk w stopce. Etykieta w jednej linii, więc przyciski mają tę samą
 * wysokość. Na telefonie przyciski idą na całą szerokość, a etykieta może
 * się złamać, żeby nie wyjść poza kartę.
 */
export const ALERT_BUTTON_SX = {
  whiteSpace: { xs: 'normal', sm: 'nowrap' },
  flexGrow: { xs: 1, sm: 0 },
} as const;

/**
 * Wcięcie treści pod nagłówkiem: ikona (24 px) i odstęp za nią (12 px).
 * Na telefonie bez wcięcia — szerokość jest tam cenniejsza.
 */
const CONTENT_INDENT = 4.5;

/** Najnowsza z chwil wykrycia (ISO); `null`, gdy żadna nie jest datą. */
export function latestDetection(ats: readonly string[]): string | null {
  let latest: string | null = null;
  let latestTime = -Infinity;
  for (const at of ats) {
    const time = new Date(at).getTime();
    if (!Number.isNaN(time) && time > latestTime) {
      latest = at;
      latestTime = time;
    }
  }
  return latest;
}

/** Lista alertów: wyraźna linia i szara linia wyjaśnienia; stonowane — szarsze. */
const AlertLines = ({
  lines,
  muted = false,
  id,
}: {
  lines: readonly OrderAlertLine[];
  muted?: boolean;
  id?: string;
}) => (
  <Box
    component="ul"
    id={id}
    sx={{ listStyle: 'none', m: 0, p: 0, mt: 0.5, display: 'grid', rowGap: 1.25 }}
  >
    {lines.map((line) => (
      <li key={line.id}>
        <Typography
          variant="body2"
          sx={muted ? { fontWeight: 400, color: 'text.secondary' } : { fontWeight: 500 }}
        >
          {line.text}
        </Typography>
        {/* Mniejsza i szara — przy czcionce bez półgrubej (Arial na
            Windows) sama waga linii alertu ich nie rozróżnia. */}
        {line.detail ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
            {line.detail}
          </Typography>
        ) : null}
      </li>
    ))}
  </Box>
);

/** Zwinięte alerty — przycisk z liczbą i lista po rozwinięciu. */
const CollapsedLines = ({ collapsed, spaced }: { collapsed: OrderAlertCollapsed; spaced: boolean }) => {
  const [open, setOpen] = useState(false);
  const listId = useId();
  const Icon = open ? ExpandLessRoundedIcon : ExpandMoreRoundedIcon;
  return (
    <Box sx={{ mt: spaced ? 1.25 : 0.5 }}>
      <Button
        size="small"
        color="inherit"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        startIcon={<Icon />}
        onClick={() => setOpen((v) => !v)}
        sx={{ color: 'text.secondary', textTransform: 'none', px: 0.5, ml: -0.5 }}
      >
        {collapsed.label}
      </Button>
      {open ? <AlertLines lines={collapsed.lines} muted id={listId} /> : null}
    </Box>
  );
};

const OriginHint = ({ text }: { text: string }) => (
  <>
    <Typography component="p" variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
      {ORIGIN_LABEL}
    </Typography>
    <Typography component="p" variant="body2">
      {text}
    </Typography>
  </>
);

export const OrderAlertCard = ({
  tone,
  title,
  detectedAt,
  origin,
  lines,
  note,
  actions,
  children,
  collapsed,
}: {
  tone: OrderAlertTone;
  title: string;
  /** Chwile wykrycia alertów (ISO) — w nagłówku najnowsza. */
  detectedAt: readonly string[];
  /** Treść dymka „Skąd ten alert?”. */
  origin: string;
  lines: readonly OrderAlertLine[];
  /** Jedno zdanie w stopce, po lewej. */
  note: ReactNode;
  /** Przyciski w stopce, po prawej. */
  actions: ReactNode;
  /** Pod alertami, nad stopką — np. pole powodu decyzji. */
  children?: ReactNode;
  /** Zwykłe alerty zwinięte w jedną linię pod ważnymi (12.10.2026). */
  collapsed?: OrderAlertCollapsed;
}) => {
  const titleId = useId();
  const Icon = TONE_ICON[tone];
  const latest = latestDetection(detectedAt);
  const main = (theme: Theme) => toneMain(theme, tone);

  return (
    <Paper
      variant="outlined"
      role="alert"
      aria-labelledby={titleId}
      sx={{
        overflow: 'hidden',
        borderColor: (theme) => alpha(main(theme), 0.35),
        borderLeftWidth: 4,
        borderLeftColor: main,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          // Tło jak w ostrzeżeniu MUI, które stoi nad kartą („Podejrzane
          // daty w TMS”) — alerty na górze karty wyglądają jak rodzina.
          bgcolor: (theme) => toneBackground(theme, tone),
        }}
      >
        {/* Ikona zostaje przy nagłówku; gdy brakuje miejsca, czas i „i”
            schodzą pod spód, do prawej. */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', columnGap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: '1 1 auto', minWidth: 0 }}>
            {/* 2 px: środek ikony (24 px) na środku pierwszej linii nagłówka (28 px). */}
            <Icon sx={{ color: main, flexShrink: 0, mt: '2px' }} />
            <Typography id={titleId} component="p" variant="subtitle1" sx={{ fontWeight: 600, minWidth: 0 }}>
              {title}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto', minHeight: 28 }}>
            {latest ? (
              <Typography
                component="time"
                dateTime={latest}
                title={fullDateTime(latest)}
                variant="body2"
                sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                wykryto {shortDateTime(latest)}
              </Typography>
            ) : null}
            <Tooltip
              title={<OriginHint text={origin} />}
              describeChild
              arrow
              placement="bottom-end"
              // Na telefonie dymek otwiera dotknięcie i zostaje do przeczytania.
              enterTouchDelay={0}
              leaveTouchDelay={10000}
              slotProps={{ tooltip: { sx: { maxWidth: 380 } } }}
            >
              <IconButton size="small" edge="end" aria-label={ORIGIN_LABEL} sx={{ color: 'text.secondary' }}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={{ pl: { xs: 0, sm: CONTENT_INDENT } }}>
          {lines.length > 0 ? <AlertLines lines={lines} /> : null}
          {collapsed && collapsed.lines.length > 0 ? (
            <CollapsedLines collapsed={collapsed} spaced={lines.length > 0} />
          ) : null}
          {children}
        </Box>
      </Box>
      <Box
        data-testid="order-alert-footer"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: 2,
          rowGap: 1,
          py: 1.25,
          pr: 2,
          pl: { xs: 2, sm: 2 + CONTENT_INDENT },
          borderTop: 1,
          borderColor: (theme) => alpha(main(theme), 0.2),
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary', flex: '1 1 280px', minWidth: 0 }}>
          {note}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            ml: 'auto',
            // Na telefonie przyciski zawsze pod zdaniem, na całą szerokość.
            flex: { xs: '1 1 100%', sm: '0 1 auto' },
          }}
        >
          {actions}
        </Box>
      </Box>
    </Paper>
  );
};
